import { AbstractHistogram } from 'hdr-histogram-js'
import { HistogramView } from '../domain/HistogramView'

export type HistogramRange = 'max' | '95' | '99' | '99.9'

const DEFAULT_PERCENTILE_RANGE: HistogramRange = '99'

export const visitHdrHistogram = (histogram: AbstractHistogram, percentileRange?: string | null): HistogramView => {
    const max = histogram.maxValue
    const total = histogram.getTotalCount()
    const selectedRange = percentileRange || DEFAULT_PERCENTILE_RANGE

    if (selectedRange === 'max') {
        return {
            buckets: createNativeBuckets(histogram, max),
            markers: createMarkers(histogram, total, max),
            total,
        }
    }

    const [lowerBound, upperBound] = resolveBounds(histogram, percentileRange, max)
    const step = estimateOptimalStep(upperBound - lowerBound)
    const alignedLowerBound = lowerBound - (lowerBound % step)
    const bucketsByStart = new Map<number, HistogramView['buckets'][number]>()

    for (let index = 0; index < histogram.countsArrayLength; index++) {
        const count = histogram.getCountAtIndex(index)
        if (count <= 0) {
            continue
        }

        const value = histogram.valueFromIndex(index)

        if (value > upperBound) {
            break
        }

        if (value < alignedLowerBound) {
            continue
        }

        const bucketStart = value - (value % step)
        const existingBucket = bucketsByStart.get(bucketStart)

        if (existingBucket) {
            existingBucket.value += count

            continue
        }

        bucketsByStart.set(bucketStart, {
            start: bucketStart,
            end: bucketStart + step,
            value: count,
        })
    }

    let padMax = upperBound
    while (bucketsByStart.size < 20) {
        const bucketStart = padMax - (padMax % step)

        if (!bucketsByStart.has(bucketStart)) {
            bucketsByStart.set(bucketStart, {
                start: bucketStart,
                end: bucketStart + step,
                value: 0,
            })
        }

        padMax = bucketStart + step
    }

    return {
        buckets: Array.from(bucketsByStart.values()).sort((first, second) => first.start - second.start),
        markers: createMarkers(histogram, total, max),
        total,
    }
}

const createNativeBuckets = (histogram: AbstractHistogram, max: number): HistogramView['buckets'] => {
    const buckets: HistogramView['buckets'] = []
    const iterator = histogram.recordedValuesIterator
    iterator.reset()

    while (iterator.hasNext()) {
        const current = iterator.next()

        if (current.valueIteratedFrom > max) {
            break
        }

        buckets.push({
            start: current.valueIteratedFrom,
            end: current.valueIteratedTo,
            value: current.countAddedInThisIterationStep,
        })
    }

    return buckets
}

const createMarkers = (histogram: AbstractHistogram, total: number, max: number): HistogramView['markers'] => {
    const median = histogram.getValueAtPercentile(50)
    const mean = Math.trunc(histogram.getMean())
    const markers: HistogramView['markers'] = [
        { name: '50%', label: 'Median', value: median },
        { name: 'Average', label: 'Average', value: mean > 0 ? mean : median },
        {
            name: '90%',
            label: '90% Percentile',
            value: total > 10 ? histogram.getValueAtPercentile(90) : max,
        },
        {
            name: '95%',
            label: '95% Percentile',
            value: total > 20 ? histogram.getValueAtPercentile(95) : max,
        },
        {
            name: '99%',
            label: '99% Percentile',
            value: total > 100 ? histogram.getValueAtPercentile(99) : max,
        },
    ]

    if (total > 1000) {
        markers.push({
            name: '99.9%',
            label: '99.9% Percentile',
            value: histogram.getValueAtPercentile(99.9),
        })
    }

    if (max > 60000) {
        markers.push({
            name: 'timeExceeded',
            label: 'timeExceeded',
            value: max,
        })
    }

    markers.push({
        name: 'Max',
        label: 'Max',
        value: max,
    })

    return markers
}

const resolveBounds = (histogram: AbstractHistogram, percentileRange: string | null | undefined, max: number): [number, number] => {
    const selectedRange = percentileRange || DEFAULT_PERCENTILE_RANGE

    const upperPercentile = selectedRange === '95' ? 95 : selectedRange === '99.9' ? 99.9 : 99
    const lowerBound = Math.trunc(histogram.getValueAtPercentile(5))
    let upperBound = Math.trunc(histogram.getValueAtPercentile(upperPercentile))

    if (upperBound <= lowerBound) {
        upperBound = max
    }

    return [lowerBound, upperBound]
}

const estimateOptimalStep = (range: number): number => {
    if (range <= 0) {
        return 2
    }

    return Math.max(Math.pow(2, Math.ceil(Math.log(range)) - 2), 2)
}
